// src/pages/CsatFeedback.jsx
// Vista consolidada de comentarios CSAT (encuesta de satisfacción) que dejan
// los clientes al cerrarse un ticket. Reemplaza a tener que entrar uno por uno.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import api from '../services/api';

// ── Design tokens (alineados con Reports.jsx) ────────────────────────────────
const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

function fmtDate(d) {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('es-MX', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    } catch { return '—'; }
}

function ratingColor(r) {
    if (r >= 4) return '#15803d';
    if (r === 3) return '#d97706';
    return '#dc2626';
}

function Stars({ value, size = 16 }) {
    const v = Math.round(Number(value) || 0);
    return (
        <span style={{ color: '#f59e0b', fontSize: size, letterSpacing: 1, lineHeight: 1 }} aria-label={`${v} de 5 estrellas`}>
            {'★'.repeat(v)}<span style={{ color: 'var(--c-text-4)' }}>{'★'.repeat(5 - v)}</span>
        </span>
    );
}

function FeedbackCard({ item }) {
    const r = item.csatRating;
    return (
        <div style={{ ...CARD, padding: '14px 18px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Stars value={r} size={18} />
                    <span style={{ fontWeight: 700, color: ratingColor(r) }}>{r}/5</span>
                    <Link to={`/tickets/${item._id}`} style={{ fontWeight: 600, color: 'var(--c-primary)', textDecoration: 'none' }}>
                        #{item.folio || '?'}
                    </Link>
                    <span style={{ color: 'var(--c-text-3)', fontSize: 13 }}>
                        {item.title}
                    </span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--c-text-4)' }}>
                    {fmtDate(item.csatAt)}
                </span>
            </div>

            {item.csatComment ? (
                <blockquote style={{
                    margin: '10px 0 8px',
                    padding: '10px 14px',
                    borderLeft: `3px solid ${ratingColor(r)}`,
                    background: 'var(--c-msg-bg, rgba(0,0,0,.03))',
                    borderRadius: 4,
                    fontStyle: 'italic',
                    color: 'var(--c-text-2)',
                    fontSize: 14,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                }}>
                    {item.csatComment}
                </blockquote>
            ) : (
                <p style={{ margin: '8px 0', color: 'var(--c-text-4)', fontSize: 13, fontStyle: 'italic' }}>
                    (sin comentario)
                </p>
            )}

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--c-text-3)', marginTop: 6 }}>
                <span><strong>Cliente:</strong> {item.createdBy?.name || '—'}</span>
                <span><strong>Agente:</strong> {item.assignedTo?.name || '—'}</span>
                <span><strong>Depto:</strong> {item.department?.name || '—'}</span>
            </div>
        </div>
    );
}

export default function CsatFeedback() {
    const [items, setItems]     = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [totalPages, setTP]   = useState(1);
    const [loading, setLoading] = useState(false);
    const [err, setErr]         = useState('');

    // Filtros
    const [ratingMax, setRatingMax]       = useState(''); // '' | '3' | '2' | '1'
    const [onlyComment, setOnlyComment]   = useState(true);
    const [fromDate, setFromDate]         = useState('');
    const [toDate, setToDate]             = useState('');

    const LIMIT = 20;

    const load = useCallback(() => {
        setLoading(true);
        setErr('');
        const params = { page, limit: LIMIT };
        if (ratingMax)   params.ratingMax    = ratingMax;
        if (onlyComment) params.onlyWithComment = '1';
        if (fromDate)    params.from         = fromDate;
        if (toDate)      params.to           = toDate;
        api.get('/tickets/csat-feedback', { params })
            .then(res => {
                setItems(res.data.items || []);
                setTotal(res.data.total || 0);
                setTP(res.data.totalPages || 1);
            })
            .catch(e => setErr(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, [page, ratingMax, onlyComment, fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    // Reset a página 1 cuando cambian filtros
    useEffect(() => { setPage(1); }, [ratingMax, onlyComment, fromDate, toDate]);

    return (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>
            <div style={{ marginBottom: 18 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--c-text-1)' }}>
                    Comentarios CSAT
                </h1>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--c-text-3)' }}>
                    Feedback que dejan los clientes al cerrarse un ticket
                </p>
            </div>

            {/* Filtros */}
            <div style={{ ...CARD, padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                    <p style={LABEL}>Rating</p>
                    <select
                        className="form-select form-select-sm"
                        value={ratingMax}
                        onChange={e => setRatingMax(e.target.value)}
                        style={{ minWidth: 140 }}
                    >
                        <option value="">Todos</option>
                        <option value="3">≤ 3 (regular o malo)</option>
                        <option value="2">≤ 2 (malo)</option>
                        <option value="1">Solo 1 (muy malo)</option>
                    </select>
                </div>

                <div>
                    <p style={LABEL}>Desde</p>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                    />
                </div>

                <div>
                    <p style={LABEL}>Hasta</p>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                        type="checkbox"
                        id="csat-only-comment"
                        className="form-check-input"
                        checked={onlyComment}
                        onChange={e => setOnlyComment(e.target.checked)}
                    />
                    <label htmlFor="csat-only-comment" style={{ fontSize: 13, color: 'var(--c-text-2)', margin: 0 }}>
                        Solo con comentario
                    </label>
                </div>

                <div style={{ flex: 1, textAlign: 'right', fontSize: 12, color: 'var(--c-text-4)' }}>
                    {loading ? <Spinner animation="border" size="sm" /> : `${total} resultado${total === 1 ? '' : 's'}`}
                </div>
            </div>

            {err && (
                <div className="alert alert-danger" style={{ fontSize: 13 }}>{err}</div>
            )}

            {/* Lista */}
            {!loading && items.length === 0 ? (
                <div style={{ ...CARD, padding: '40px 20px', textAlign: 'center', color: 'var(--c-text-3)' }}>
                    No hay comentarios CSAT con los filtros actuales.
                </div>
            ) : (
                items.map(it => <FeedbackCard key={it._id} item={it} />)
            )}

            {/* Paginación */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 18 }}>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        ← Anterior
                    </button>
                    <span style={{ fontSize: 13, color: 'var(--c-text-3)' }}>
                        Página {page} de {totalPages}
                    </span>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={page >= totalPages || loading}
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    >
                        Siguiente →
                    </button>
                </div>
            )}
        </div>
    );
}
